import React, { useEffect } from 'react';
import { CheckCircle, XCircle, Clock, AlertTriangle, X } from 'lucide-react';

const StatusDialog = ({ 
  isOpen, 
  onClose, 
  title, 
  message, 
  status = 'info', // 'success', 'error', 'warning', 'info', 'loading'
  details = null,
  actions = null 
}) => {
  const handleClose = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (process.env.NODE_ENV === 'development') {
      console.log('StatusDialog: Close button clicked', { onClose: !!onClose, isOpen });
    }
    if (onClose) {
      onClose();
    } else {
      console.error('StatusDialog: onClose function is not provided!');
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      if (process.env.NODE_ENV === 'development') {
        console.log('StatusDialog: Backdrop clicked');
      }
      if (onClose) {
        onClose();
      }
    }
  };

  // Handle Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        if (process.env.NODE_ENV === 'development') {
          console.log('StatusDialog: Escape key pressed');
        }
        if (onClose) {
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const getStatusIcon = () => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-8 h-8 text-green-500" />;
      case 'error':
        return <XCircle className="w-8 h-8 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="w-8 h-8 text-yellow-500" />;
      case 'loading':
        return <Clock className="w-8 h-8 text-blue-500 animate-spin" />;
      default:
        return <AlertTriangle className="w-8 h-8 text-blue-500" />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'success':
        return 'border-green-500 bg-green-900/20';
      case 'error':
        return 'border-red-500 bg-red-900/20';
      case 'warning':
        return 'border-yellow-500 bg-yellow-900/20';
      case 'loading':
        return 'border-blue-500 bg-blue-900/20';
      default:
        return 'border-blue-500 bg-blue-900/20';
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className={`bg-gray-900 rounded-lg border-2 ${getStatusColor()} max-w-md w-full`}>
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              {getStatusIcon()}
              <h3 className="text-xl font-semibold text-white">{title}</h3>
            </div>
            <button
              onClick={handleClose}
              onMouseDown={handleClose}
              className="text-gray-400 hover:text-white transition-colors p-2 rounded hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
              type="button"
              title="Close dialog (or press Escape)"
              aria-label="Close dialog"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          <div className="mb-4">
            <p className="text-white text-opacity-90">{message}</p>
            {details && (
              <div className="mt-3 p-3 bg-gray-800 rounded-lg">
                <p className="text-gray-300 text-sm">{details}</p>
              </div>
            )}
          </div>
          
          {actions && (
            <div className="flex gap-3 justify-end">
              {actions}
            </div>
          )}
          
          {/* Close instruction */}
          <div className="mt-4 pt-3 border-t border-gray-700">
            <p className="text-gray-400 text-xs text-center">
              Click X, press Escape, or click outside to close
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatusDialog;
